import { useState, useEffect, useCallback, useRef } from 'react';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';

export const useTicketsList = (
  filterTab = 'todos',
  idUsuarioAsignado = null,
  enabled = true,
  initialPageSize = 20,
  onInitialLoaded = null,
  dFechaInicio = null,
  dFechaFin = null,
  searchTerm = '',
  idEtiqueta = null,
  idCatNivelSoporte = null
) => {
  // Caché de tickets por pestaña, usuario, fechas, búsqueda, etiqueta y página: { [tab_userId_fInicio_fFin_search_etiqueta_pX_sY]: [...] }
  const [tabCache, setTabCache] = useState({});
  const [counts, setCounts] = useState({ todos: 0, pendientes: 0, enProgreso: 0, cerrados: 0 });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const { token, idUsuario } = useToken();
  const { showErrorToast } = useToast();
  const idUsuarioRef = useRef(idUsuario);
  idUsuarioRef.current = idUsuario;

  const cacheRef = useRef({});
  const inFlightRef = useRef(new Set());
  const toastRef = useRef(showErrorToast);
  toastRef.current = showErrorToast;
  const refreshCountsTimeoutRef = useRef(null);

  const activeTabRef = useRef(filterTab);
  activeTabRef.current = filterTab;
  const activeAgentRef = useRef(idUsuarioAsignado);
  activeAgentRef.current = idUsuarioAsignado;
  const activeFechaInicioRef = useRef(dFechaInicio);
  activeFechaInicioRef.current = dFechaInicio;
  const activeFechaFinRef = useRef(dFechaFin);
  activeFechaFinRef.current = dFechaFin;
  const activeSearchRef = useRef(searchTerm);
  activeSearchRef.current = searchTerm;
  const activeEtiquetaRef = useRef(idEtiqueta);
  activeEtiquetaRef.current = idEtiqueta;
  const activeNivelRef = useRef(idCatNivelSoporte);
  activeNivelRef.current = idCatNivelSoporte;
  const pageRef = useRef(page);
  pageRef.current = page;
  const pageSizeRef = useRef(pageSize);
  pageSizeRef.current = pageSize;
  const lastActivitySenderRef = useRef({});
  const clientUnreadMapRef = useRef({});

  const getCacheKey = (tab, agent, fInicio, fFin, search, etiqueta, nivel, pNum, sizeNum) => {
    const hasAgent = agent && agent !== 'todos' && agent !== 'all';
    const dateKey = `${fInicio || 'all'}_${fFin || 'all'}`;
    const searchKey = search ? search.trim().toLowerCase() : 'all';
    const etiquetaKey = etiqueta ? String(etiqueta) : 'all';
    const nivelKey = (nivel !== null && nivel !== undefined && nivel !== '') ? String(nivel) : 'all';
    return `${tab}_${hasAgent ? agent : 'all'}_${dateKey}_${searchKey}_${etiquetaKey}_${nivelKey}_p${pNum}_s${sizeNum}`;
  };

  const onInitialLoadedRef = useRef(onInitialLoaded);
  onInitialLoadedRef.current = onInitialLoaded;
  const initialLoadedFiredRef = useRef(false);
  const notifyInitialLoaded = () => {
    if (!initialLoadedFiredRef.current && onInitialLoadedRef.current) {
      initialLoadedFiredRef.current = true;
      onInitialLoadedRef.current();
    }
  };

  const getEstadoParam = tab => {
    if (tab === 'pendientes') return 1;
    if (tab === 'en_progreso') return 3;
    if (tab === 'cerrados' || tab === 'resueltos') return 2;
    return null; // 'todos'
  };



  const getReadTicketsMap = () => {
    try {
      const stored = localStorage.getItem('support_engine_read_tickets');
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  };

  const markTicketAsRead = useCallback((idTicket) => {
    if (!idTicket) return;
    const numId = Number(idTicket);
    clientUnreadMapRef.current[numId] = false;
    try {
      const currentMap = getReadTicketsMap();
      currentMap[numId] = new Date().toISOString();
      localStorage.setItem('support_engine_read_tickets', JSON.stringify(currentMap));
    } catch (e) {
      console.warn('Error al guardar ticket leido en localStorage:', e);
    }

    setTabCache(prev => {
      let hasChanges = false;
      Object.keys(prev).forEach(key => {
        if (Array.isArray(prev[key])) {
          if (prev[key].some(t => Number(t.idTicket) === numId && t.bSinLeer !== false)) {
            hasChanges = true;
          }
        }
      });
      if (!hasChanges) return prev;

      const updated = { ...prev };
      Object.keys(updated).forEach(key => {
        if (Array.isArray(updated[key])) {
          updated[key] = updated[key].map(t =>
            Number(t.idTicket) === numId ? { ...t, bSinLeer: false } : t
          );
        }
      });
      return updated;
    });

    Object.keys(cacheRef.current).forEach(key => {
      if (Array.isArray(cacheRef.current[key])) {
        cacheRef.current[key] = cacheRef.current[key].map(t =>
          Number(t.idTicket) === numId ? { ...t, bSinLeer: false } : t
        );
      }
    });
  }, []);



  const fetchTicketsForTab = useCallback(async (tab, pageNum = 1, sizeNum = 20, force = false) => {
    if (!token) return;

    const hasAgentFilter = activeAgentRef.current && activeAgentRef.current !== 'todos' && activeAgentRef.current !== 'all';
    const cacheKey = getCacheKey(
      tab,
      activeAgentRef.current,
      activeFechaInicioRef.current,
      activeFechaFinRef.current,
      activeSearchRef.current,
      activeEtiquetaRef.current,
      activeNivelRef.current,
      pageNum,
      sizeNum
    );

    // Si ya está en curso una petición idéntica para esta clave, no duplicar (a menos que sea recarga forzada)
    if (inFlightRef.current.has(cacheKey) && !force) {
      return;
    }

    // Si ya tenemos los datos en caché y no es recarga forzada:
    // Mostramos los datos de inmediato para evitar trabas o saltos visuales (0ms)
    const cachedData = cacheRef.current[cacheKey];
    if (cachedData && !force) {
      const total = cachedData.length > 0 && cachedData[0].iTotalRegistros != null
        ? Number(cachedData[0].iTotalRegistros)
        : cachedData.length;
      setTotalRegistros(total);
      notifyInitialLoaded();
      // No activamos el spinner bloqueante si ya tenemos datos para mostrar
    } else {
      setIsLoading(true);
    }

    inFlightRef.current.add(cacheKey);
    setError(null);

    try {
      const estado = getEstadoParam(tab);
      const params = new URLSearchParams();
      if (estado) params.append('iEstadoTicket', estado);
      if (hasAgentFilter) params.append('idUsuarioAsignado', activeAgentRef.current);
      if (idUsuarioRef.current) params.append('idUsuarioActual', idUsuarioRef.current);
      if (activeFechaInicioRef.current) params.append('dFechaInicio', activeFechaInicioRef.current);
      if (activeFechaFinRef.current) params.append('dFechaFin', activeFechaFinRef.current);
      if (activeSearchRef.current) params.append('searchTerm', activeSearchRef.current.trim());
      if (activeEtiquetaRef.current) params.append('idEtiqueta', activeEtiquetaRef.current);
      if (activeNivelRef.current !== null && activeNivelRef.current !== undefined && activeNivelRef.current !== '') {
        params.append('idCatNivelSoporte', activeNivelRef.current);
      }
      params.append('pageNumber', pageNum);
      params.append('pageSize', sizeNum);

      const url = `/api/SupportEngine/GetTicketsList?${params.toString()}`;

      const response = await fetch(url, {
        headers: {
          Accept: 'application/json',
          Authorization: 'Bearer ' + token
        }
      });

      if (!response.ok) {
        let errorDetails = '';
        try {
          const errJson = await response.json();
          errorDetails = errJson.message || errJson.details || JSON.stringify(errJson);
        } catch {
          errorDetails = await response.text();
        }
        throw new Error(`Error ${response.status}: ${errorDetails || 'No se pudieron cargar los tickets'}`);
      }

      const result = await response.json();
      const data = result.data || result.tickets || (Array.isArray(result) ? result : []);
      const ticketList = Array.isArray(data) ? data : [];

      const readMap = getReadTicketsMap();
      const enrichedTicketList = ticketList.map(t => {
        const id = Number(t.idTicket);
        const lastRead = readMap[id];
        const isLastAgent = lastActivitySenderRef.current[id] === true;
        const hasLiveClientUnread = clientUnreadMapRef.current[id] === true;

        // Es sin leer si recibimos actividad de cliente en vivo, o si en BD es estado 3 con actividad posterior a lectura
        const isClientUnread = !isLastAgent && (
          hasLiveClientUnread ||
          (Number(t.iEstadoTicket) === 3 && (lastRead
            ? new Date(t.dUltimaActividad || t.dCreatedDate) > new Date(lastRead)
            : true))
        );

        return {
          ...t,
          bSinLeer: isLastAgent ? false : isClientUnread
        };
      });

      // Guardar en la caché bajo la clave combinada de página y tamaño
      cacheRef.current[cacheKey] = enrichedTicketList;
      setTabCache(prev => ({
        ...prev,
        [cacheKey]: enrichedTicketList
      }));

      // Total de registros desde la BD (retornado por el SP en iTotalRegistros) o longitud de la lista
      const totalCount = ticketList.length > 0 && ticketList[0].iTotalRegistros != null
        ? Number(ticketList[0].iTotalRegistros)
        : ticketList.length;

      setTotalRegistros(totalCount);

      const tabKeyMap = {
        todos: 'todos',
        pendientes: 'pendientes',
        en_progreso: 'enProgreso',
        cerrados: 'cerrados',
        resueltos: 'cerrados'
      };
      const currentTabKey = tabKeyMap[tab];
      if (currentTabKey) {
        setCounts(prev => ({
          ...prev,
          [currentTabKey]: totalCount
        }));
      }

      // Actualizar conteos directamente desde la respuesta de GetTicketsList si vienen (3er SELECT)
      const countsData = result.counts || (result.data && !Array.isArray(result.data) ? result.data.counts : null);
      if (countsData) {
        setCounts({
          todos: Number(countsData.totalAll ?? countsData.iTotalTodos ?? countsData.totalTodos ?? 0),
          pendientes: Number(countsData.totalPending ?? countsData.iTotalPendientes ?? countsData.totalPendientes ?? 0),
          enProgreso: Number(countsData.totalInProgress ?? countsData.iTotalEnProgreso ?? countsData.totalEnProgreso ?? 0),
          cerrados: Number(countsData.totalClosed ?? countsData.iTotalCerrados ?? countsData.totalCerrados ?? 0)
        });
      }
    } catch (err) {
      setError(err.message || 'Error al obtener tickets');
      if (toastRef.current) {
        toastRef.current('Error', 'No se pudieron sincronizar los tickets de soporte');
      }
    } finally {
      inFlightRef.current.delete(cacheKey);
      setIsLoading(false);
      notifyInitialLoaded();
    }
  }, [token]);

  const countsRef = useRef(counts);
  countsRef.current = counts;

  // Un solo efecto para controlar cambios de pestaña, usuario asignado, fechas o página sin carreras paralelas
  useEffect(() => {
    if (!enabled) {
      cacheRef.current = {};
      return;
    }

    const isFilterOrAgentOrDateOrSearchChange =
      activeTabRef.current !== filterTab ||
      activeAgentRef.current !== idUsuarioAsignado ||
      activeFechaInicioRef.current !== dFechaInicio ||
      activeFechaFinRef.current !== dFechaFin ||
      activeSearchRef.current !== searchTerm ||
      activeEtiquetaRef.current !== idEtiqueta ||
      activeNivelRef.current !== idCatNivelSoporte;

    activeTabRef.current = filterTab;
    activeAgentRef.current = idUsuarioAsignado;
    activeFechaInicioRef.current = dFechaInicio;
    activeFechaFinRef.current = dFechaFin;
    activeSearchRef.current = searchTerm;
    activeEtiquetaRef.current = idEtiqueta;
    activeNivelRef.current = idCatNivelSoporte;

    if (isFilterOrAgentOrDateOrSearchChange) {
      const currentCounts = countsRef.current;
      const targetCount = filterTab === 'pendientes'
        ? currentCounts.pendientes
        : filterTab === 'en_progreso'
        ? currentCounts.enProgreso
        : (filterTab === 'cerrados' || filterTab === 'resueltos')
        ? currentCounts.cerrados
        : currentCounts.todos;
      setTotalRegistros(targetCount ?? 0);

      // Limpiar caché previa de esta pestaña al cambiar de tab, fechas, búsqueda, etiqueta o nivel para evitar mostrar datos viejos
      const newCacheKey = getCacheKey(filterTab, idUsuarioAsignado, dFechaInicio, dFechaFin, searchTerm, idEtiqueta, idCatNivelSoporte, 1, pageSize);
      delete cacheRef.current[newCacheKey];
      setTabCache(prev => {
        if (!prev[newCacheKey]) return prev;
        const next = { ...prev };
        delete next[newCacheKey];
        return next;
      });

      if (page !== 1) {
        setPage(1);
        // Al resetear page a 1, el siguiente ciclo ejecutará este efecto con la página 1
        return;
      }
    }

    fetchTicketsForTab(filterTab, page, pageSize, isFilterOrAgentOrDateOrSearchChange);
  }, [filterTab, idUsuarioAsignado, dFechaInicio, dFechaFin, searchTerm, idEtiqueta, idCatNivelSoporte, page, pageSize, fetchTicketsForTab, enabled]);

  const reloadTickets = useCallback(() => {
    if (!enabled) return;
    cacheRef.current = {};
    setTabCache({});
    fetchTicketsForTab(activeTabRef.current, pageRef.current, pageSizeRef.current, true);
  }, [fetchTicketsForTab, enabled]);

  // Refresco de contadores reales directamente desde el SP del servidor (una sola fuente de verdad)
  const refreshCounts = useCallback(async () => {
    if (!enabled || !token) return;
    try {
      const hasAgentFilter = activeAgentRef.current && activeAgentRef.current !== 'todos' && activeAgentRef.current !== 'all';
      const params = new URLSearchParams();
      if (hasAgentFilter) params.append('idUsuarioAsignado', activeAgentRef.current);
      if (idUsuarioRef.current) params.append('idUsuarioActual', idUsuarioRef.current);
      if (activeFechaInicioRef.current) params.append('dFechaInicio', activeFechaInicioRef.current);
      if (activeFechaFinRef.current) params.append('dFechaFin', activeFechaFinRef.current);
      if (activeSearchRef.current) params.append('searchTerm', activeSearchRef.current.trim());
      if (activeEtiquetaRef.current) params.append('idEtiqueta', activeEtiquetaRef.current);
      if (activeNivelRef.current !== null && activeNivelRef.current !== undefined && activeNivelRef.current !== '') {
        params.append('idCatNivelSoporte', activeNivelRef.current);
      }
      params.append('pageNumber', 1);
      params.append('pageSize', 1);

      const resp = await fetch(`/api/SupportEngine/GetTicketsList?${params.toString()}`, {
        headers: { Accept: 'application/json', Authorization: 'Bearer ' + token }
      });
      if (resp.ok) {
        const result = await resp.json();
        const countsData = result.counts || (result.data && !Array.isArray(result.data) ? result.data.counts : null);
        if (countsData) {
          setCounts({
            todos: Number(countsData.iTotalTodos ?? countsData.totalTodos ?? 0),
            pendientes: Number(countsData.iTotalPendientes ?? countsData.totalPendientes ?? 0),
            enProgreso: Number(countsData.iTotalEnProgreso ?? countsData.totalEnProgreso ?? 0),
            cerrados: Number(countsData.iTotalCerrados ?? countsData.totalCerrados ?? 0)
          });
        }
      }
    } catch (e) {
      console.warn('Error al refrescar conteos de tickets:', e);
    }
  }, [enabled, token]);



  const reloadDebounceRef = useRef(null);

  useEffect(() => {
    return () => {
      if (refreshCountsTimeoutRef.current) {
        clearTimeout(refreshCountsTimeoutRef.current);
      }
      if (reloadDebounceRef.current) {
        clearTimeout(reloadDebounceRef.current);
      }
    };
  }, []);

  // Manejo reactivo de actividad en tickets (mensajes entrantes / salientes / cambios de asignación)
  // Al recibir actividad, recargamos directamente la pestaña visible y las estadísticas/conteos
  // desde el servidor (una sola fuente de verdad), eliminando la lógica compleja de parcheo en memoria.
  const handleTicketActivity = useCallback((event) => {
    if (!enabled || !token) return;
    const detail = event?.detail;
    const idTicket = Number(detail?.idTicket);
    if (!idTicket) return;

    const isFromAgent = detail?.bEsAgente === 1 || detail?.bEsAgente === true;
    lastActivitySenderRef.current[idTicket] = isFromAgent;

    if (isFromAgent) {
      // Si el mensaje es del agente, marcar como leído inmediatamente
      clientUnreadMapRef.current[idTicket] = false;
      markTicketAsRead(idTicket);
    } else {
      // Si el mensaje es del cliente de WhatsApp: marcar como no leído reactivamente en 0ms
      clientUnreadMapRef.current[idTicket] = true;
      try {
        const currentMap = getReadTicketsMap();
        delete currentMap[idTicket];
        localStorage.setItem('support_engine_read_tickets', JSON.stringify(currentMap));
      } catch (e) {
        console.warn('Error al actualizar lectura en localStorage:', e);
      }

      // Actualizar inmediatamente en la caché visible para pintar la card verde sin esperar al fetch
      setTabCache(prev => {
        let hasTicket = false;
        Object.keys(prev).forEach(key => {
          if (Array.isArray(prev[key]) && prev[key].some(t => Number(t.idTicket) === idTicket)) {
            hasTicket = true;
          }
        });
        if (!hasTicket) return prev;

        const updated = { ...prev };
        Object.keys(updated).forEach(key => {
          if (Array.isArray(updated[key])) {
            updated[key] = updated[key].map(t =>
              Number(t.idTicket) === idTicket
                ? {
                    ...t,
                    bSinLeer: true,
                    vUltimoMensaje: detail?.vTextoMensaje || t.vUltimoMensaje,
                    dUltimaActividad: detail?.dUltimaActividad || new Date().toISOString()
                  }
                : t
            );
          }
        });
        return updated;
      });

      Object.keys(cacheRef.current).forEach(key => {
        if (Array.isArray(cacheRef.current[key])) {
          cacheRef.current[key] = cacheRef.current[key].map(t =>
            Number(t.idTicket) === idTicket
              ? {
                  ...t,
                  bSinLeer: true,
                  vUltimoMensaje: detail?.vTextoMensaje || t.vUltimoMensaje,
                  dUltimaActividad: detail?.dUltimaActividad || new Date().toISOString()
                }
              : t
          );
        }
      });
    }

    if (reloadDebounceRef.current) {
      clearTimeout(reloadDebounceRef.current);
    }

    reloadDebounceRef.current = setTimeout(() => {
      // 1. Limpiar caché de otras pestañas para obligarlas a refrescar cuando se visiten
      Object.keys(cacheRef.current).forEach(k => {
        if (!k.startsWith(activeTabRef.current)) {
          delete cacheRef.current[k];
        }
      });

      // 2. Recargar tickets de la pestaña activa directamente desde el servidor (reemplazo limpio sin parpadeos)
      fetchTicketsForTab(activeTabRef.current, pageRef.current, pageSizeRef.current, true);

      // 3. Recargar los contadores/estadísticas de todas las pestañas
      refreshCounts();
    }, 200);
  }, [enabled, token, fetchTicketsForTab, refreshCounts, markTicketAsRead]);

  // Listener local de eventos DOM en ventana (estable, no se remueve en cada cambio de tab)
  useEffect(() => {
    window.addEventListener('akasia:support:ticket_activity', handleTicketActivity);
    return () => {
      window.removeEventListener('akasia:support:ticket_activity', handleTicketActivity);
    };
  }, [handleTicketActivity]);

  // Listener para eventos de lectura cuando el agente abre el ticket o responde
  useEffect(() => {
    const handleExternalRead = e => {
      const idTicket = Number(e?.detail?.idTicket);
      if (idTicket) {
        markTicketAsRead(idTicket);
      }
    };
    window.addEventListener('akasia:support:ticket_read', handleExternalRead);
    return () => {
      window.removeEventListener('akasia:support:ticket_read', handleExternalRead);
    };
  }, [markTicketAsRead]);

  const currentCacheKey = getCacheKey(
    filterTab,
    idUsuarioAsignado,
    dFechaInicio,
    dFechaFin,
    searchTerm,
    idEtiqueta,
    idCatNivelSoporte,
    page,
    pageSize
  );
  const currentTickets = tabCache[currentCacheKey] || cacheRef.current[currentCacheKey] || [];
  const totalPages = Math.max(1, Math.ceil(totalRegistros / pageSize));

  return {
    tickets: currentTickets,
    counts,
    isLoading,
    error,
    page,
    setPage,
    pageSize,
    setPageSize,
    totalRegistros,
    totalPages,
    reloadTickets,
    markTicketAsRead
  };
};

export default useTicketsList;

